import React, { useEffect, useState, useCallback } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import Text from "@components/Text";
import Tabs from "@components/Tabs";
import Button from "@components/Button";
import Spinner from "@components/Spinner";
import { Green, Gray } from "@components/Dot";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { isDeviceOnline, grafanaDeviceUrl } from "@src/utils/deviceOnline";
import {
  getDeviceDetail,
  updateDevice,
  sendDeviceRequest,
  AdminDeviceDetail,
} from "@services/adminService";
import { ROUTES } from "@stores/AccountStore";
import DeviceInfoSubTab from "./DeviceInfoSubTab";
import DeviceMetricsSubTab from "./DeviceMetricsSubTab";
import DeviceCalibrationsSubTab from "./DeviceCalibrationsSubTab";
import DeviceAnalyticsSettingsSubTab from "./DeviceAnalyticsSettingsSubTab";
import AdminMessagesTab from "./AdminMessagesTab";
import DeviceSimCard from "./DeviceSimCard";
import EditDeviceDialog from "./EditDeviceDialog";

const BackRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  cursor: pointer;
  user-select: none;
  &:hover span {
    text-decoration: underline;
  }
`;

const BackArrow = styled.span`
  font-size: 16px;
  color: #4a90d9;
`;

const BackText = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  color: #4a90d9;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ActionButton = styled(Button)`
  width: auto;
  min-width: auto;
  padding: 0 16px;
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const ErrorWrap = styled.div`
  text-align: center;
  padding: 48px 0;
`;

const SUB_TABS = [
  { name: "Device" },
  { name: "Analytics Settings" },
  { name: "Metrics" },
  { name: "Calibrations" },
  { name: "Messages" },
  { name: "Hologram" },
];

/** Imported John Deere combines have no MQTT messages and no SIM. */
const MQTT_ONLY_TABS = new Set(["Messages", "Hologram"]);

interface Props {
  esn: string;
}

const AdminDeviceDetailPage: React.FC<Props> = ({ esn }) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<AdminDeviceDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const loadDetail = useCallback(() => {
    setLoading(true);
    setError(null);
    getDeviceDetail(esn)
      .then(setDetail)
      .catch((e) => setError(e.message || "Failed to load device"))
      .finally(() => setLoading(false));
  }, [esn]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const handleSave = async (updates: Record<string, any>) => {
    await updateDevice(esn, updates);
    loadDetail();
  };

  const handleSendRequest = async () => {
    try {
      await sendDeviceRequest(esn, { type: "settings_sync" });
      loadDetail();
    } catch (e) {
      console.error("Failed to send request:", e);
    }
  };

  if (loading) {
    return (
      <LoadingWrap>
        <Spinner />
      </LoadingWrap>
    );
  }

  if (error || !detail) {
    return (
      <ErrorWrap>
        <Text size={14} color="#e74c3c">
          {error || "Device not found"}
        </Text>
      </ErrorWrap>
    );
  }

  const isJohnDeere = detail.device.source === "johndeere";
  const tabs = isJohnDeere ? SUB_TABS.filter((t) => !MQTT_ONLY_TABS.has(t.name)) : SUB_TABS;
  const activeName = tabs[activeTab]?.name ?? "Device";

  return (
    <div>
      <BackRow onClick={() => navigate(ROUTES.ADMIN)}>
        <BackArrow>&larr;</BackArrow>
        <BackText>Back to devices</BackText>
      </BackRow>

      <HeaderRow>
        <TitleRow>
          {isDeviceOnline(detail.status?.lastConnectedAt) ? <Green /> : <Gray />}
          <Text size={18} type="heading" color="#181b1f">
            {detail.device.alias || detail.device.esn}
          </Text>
          {detail.device.alias && (
            <Text size={13} color="#899093">
              {detail.device.esn}
            </Text>
          )}
        </TitleRow>
        <ActionButtons>
          {!isJohnDeere && (
            <ActionButton kind="outline" onClick={handleSendRequest}>
              Send request
            </ActionButton>
          )}
          <ActionButton kind="outline" onClick={() => setEditOpen(true)}>
            Edit
          </ActionButton>
          <ActionButton
            kind="outline"
            onClick={() =>
              window.open(
                grafanaDeviceUrl(esn ?? ""),
                "_blank",
                "noopener,noreferrer"
              )
            }
          >
            Grafana
          </ActionButton>
        </ActionButtons>
      </HeaderRow>

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActive={setActiveTab}
        style={{ marginBottom: 24 }}
      />

      {activeName === "Device" && (
        <DeviceInfoSubTab detail={detail} onRefreshDetail={loadDetail} />
      )}
      {activeName === "Analytics Settings" && (
        <DeviceAnalyticsSettingsSubTab esn={esn} detail={detail} />
      )}
      {activeName === "Metrics" && <DeviceMetricsSubTab esn={esn} />}
      {activeName === "Calibrations" && <DeviceCalibrationsSubTab esn={esn} />}
      {activeName === "Messages" && <AdminMessagesTab fixedDeviceEsn={esn} />}
      {activeName === "Hologram" && (
        <DeviceSimCard esn={esn} iccid={detail.device.iccid} />
      )}

      <EditDeviceDialog
        visible={editOpen}
        device={detail.device}
        onClose={() => setEditOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
};

export default AdminDeviceDetailPage;
